import React, { useState, useEffect } from 'react';
import { X, Copy, CheckCircle, Clock, AlertCircle, Bitcoin, Mail, Send } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import './PaymentModal.css';

const FIAT_CURRENCIES = [
  { code: 'usd', label: 'USD', symbol: '$' },
  { code: 'eur', label: 'EUR', symbol: '€' },
  { code: 'gbp', label: 'GBP', symbol: '£' },
  { code: 'cad', label: 'CAD', symbol: 'CA$' },
  { code: 'aud', label: 'AUD', symbol: 'AU$' },
  { code: 'jpy', label: 'JPY', symbol: '¥' },
  { code: 'chf', label: 'CHF', symbol: 'CHF ' },
  { code: 'inr', label: 'INR', symbol: '₹' },
];

const fiatSymbol = (code) => FIAT_CURRENCIES.find(c => c.code === code)?.symbol || '$';
const fiatLabel = (code) => (FIAT_CURRENCIES.find(c => c.code === code)?.label) || (code || 'USD').toUpperCase();

const PaymentModal = ({ isOpen, onClose, onPaymentCreated }) => {
  const [step, setStep] = useState('create'); // create, processing, payment
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'usd',
    pay_currency: 'btc',
    description: '',
    customer_email: '',
    send_email: false,
  });
  const [payment, setPayment] = useState(null);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState('');

  // Fetch available cryptocurrencies
  useEffect(() => {
    if (isOpen) {
      fetchAvailableCurrencies();
    }
  }, [isOpen]);

  // Fetch conversion estimate when amount or currency changes
  useEffect(() => {
    if (formData.amount && formData.pay_currency) {
      fetchEstimate();
    }
  }, [formData.amount, formData.pay_currency]);

  const fetchAvailableCurrencies = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/currencies/available`);
      if (response.ok) {
        const data = await response.json();
        setAvailableCurrencies(data);
      }
    } catch (err) {
      console.error('Error fetching currencies:', err);
    }
  };

  const fetchEstimate = async () => {
    if (!formData.amount || formData.amount <= 0) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/estimate?amount=${formData.amount}&currency_from=${formData.currency}&currency_to=${formData.pay_currency}`
      );
      if (response.ok) {
        const data = await response.json();
        setEstimate(data);
      }
    } catch (err) {
      console.error('Error fetching estimate:', err);
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const useLinkEndpoint = !!formData.customer_email || formData.send_email;
      const endpoint = useLinkEndpoint ? '/api/merchant/payment-links' : '/api/payments/create';
      const body = {
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        pay_currency: formData.pay_currency,
        description: formData.description,
      };
      if (useLinkEndpoint) {
        body.customer_email = formData.customer_email || null;
        body.send_email = !!formData.send_email;
      }
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create payment');
      }

      const paymentData = await response.json();
      setPayment(paymentData);
      setStep('payment');

      if (formData.send_email && formData.customer_email) {
        setEmailSentTo(formData.customer_email);
      }
      
      if (onPaymentCreated) {
        onPaymentCreated(paymentData);
      }

      // Start polling for payment status
      pollPaymentStatus(paymentData.id);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (paymentId) => {
    const interval = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/${paymentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setPayment(data);

          // Stop polling if payment is completed or failed
          if (['paid', 'completed', 'failed', 'expired'].includes(data.payment_status)) {
            clearInterval(interval);
          }
        }
      } catch (err) {
        console.error('Error polling payment status:', err);
      }
    }, 5000); // Poll every 5 seconds

    // Clean up interval on unmount
    return () => clearInterval(interval);
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts / older browsers
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Link copied to clipboard');
    } catch (e) {
      toast.error('Could not copy — please copy manually');
    }
  };

  const handleClose = () => {
    setStep('create');
    setFormData({ amount: '', currency: 'usd', pay_currency: 'btc', description: '', customer_email: '', send_email: false });
    setPayment(null);
    setEstimate(null);
    setError('');
    setEmailSentTo('');
    onClose();
  };

  const sendEmailNow = async () => {
    if (!payment || !formData.customer_email) {
      toast.error('Please enter a customer email');
      return;
    }
    setEmailSending(true);
    try {
      const token = localStorage.getItem('token');
      // Re-create as link with send_email - backend will queue email
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/merchant/payment-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          amount: payment.base_amount || payment.price_amount,
          currency: payment.price_currency,
          pay_currency: payment.pay_currency,
          description: payment.description,
          customer_email: formData.customer_email,
          send_email: true,
        }),
      });
      if (r.ok) {
        toast.success(`Email sent to ${formData.customer_email}`);
        setEmailSentTo(formData.customer_email);
      } else {
        const e = await r.json();
        toast.error(e.detail || 'Failed to send email');
      }
    } catch (e) {
      toast.error('Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
      case 'completed':
        return <CheckCircle size={24} className="status-icon success" />;
      case 'pending':
      case 'confirming':
        return <Clock size={24} className="status-icon pending" />;
      case 'failed':
      case 'expired':
        return <AlertCircle size={24} className="status-icon error" />;
      default:
        return <Clock size={24} className="status-icon pending" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Waiting for payment';
      case 'confirming': return 'Confirming transaction';
      case 'paid': return 'Payment received';
      case 'completed': return 'Payment completed';
      case 'failed': return 'Payment failed';
      case 'expired': return 'Payment expired';
      default: return status;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="payment-modal-overlay" onClick={handleClose}>
      <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>
          <X size={24} />
        </button>

        {step === 'create' && (
          <div className="payment-form">
            <h2 className="modal-title">
              <Bitcoin size={28} />
              Create Crypto Payment
            </h2>

            <form onSubmit={handleCreatePayment}>
              <div className="form-group">
                <label>Amount</label>
                <div className="input-with-currency">
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="100.00"
                    required
                    data-testid="payment-amount-input"
                  />
                  <select
                    className="currency-select"
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    data-testid="fiat-currency-select"
                  >
                    {FIAT_CURRENCIES.map(c => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Accept Cryptocurrency</label>
                <select
                  value={formData.pay_currency}
                  onChange={(e) => setFormData({ ...formData, pay_currency: e.target.value })}
                  required
                  data-testid="crypto-currency-select"
                >
                  <option value="btc">Bitcoin (BTC)</option>
                  <option value="eth">Ethereum (ETH)</option>
                  <option value="ltc">Litecoin (LTC)</option>
                  <option value="usdt">Tether (USDT)</option>
                  <option value="usdc">USD Coin (USDC)</option>
                  {availableCurrencies.slice(5).map(curr => (
                    <option key={curr.code} value={curr.code}>
                      {curr.code.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              {estimate && formData.amount > 0 && (
                <div className="conversion-info">
                  <div className="conversion-label">Customer will pay:</div>
                  <div className="conversion-amount">
                    {estimate.estimated_amount} {formData.pay_currency.toUpperCase()}
                  </div>
                  <div className="conversion-rate">
                    Rate: 1 {formData.pay_currency.toUpperCase()} = {fiatSymbol(formData.currency)}{(1 / estimate.rate).toFixed(2)} {fiatLabel(formData.currency)}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Description (Optional)</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Order #123, Product name, etc."
                  data-testid="payment-description-input"
                />
              </div>

              <div className="form-group">
                <label>
                  <Mail size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                  Customer Email (optional — for sharing the link)
                </label>
                <input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                  placeholder="customer@example.com"
                  data-testid="customer-email-input"
                />
              </div>

              {formData.customer_email && (
                <label className="checkbox-row" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={formData.send_email}
                    onChange={(e) => setFormData({ ...formData, send_email: e.target.checked })}
                    data-testid="send-email-checkbox"
                  />
                  <span style={{ fontSize: 13 }}>
                    Send the payment link to {formData.customer_email} now
                  </span>
                </label>
              )}

              <div className="fee-notice">
                Platform fee (2%) will be automatically deducted from settlement
              </div>

              {error && <div className="error-message">{error}</div>}

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Creating Payment...' : 'Generate Payment'}
              </button>
            </form>
          </div>
        )}

        {step === 'payment' && payment && (
          <div className="payment-display">
            <div className="payment-status">
              {getStatusIcon(payment.payment_status)}
              <h3>{getStatusText(payment.payment_status)}</h3>
            </div>

            {(payment.payment_status === 'pending' || payment.payment_status === 'confirming') && (
              <>
                <div className="qr-code-container">
                  <QRCodeSVG
                    value={payment.pay_address || payment.payment_url || ''}
                    size={256}
                    level="H"
                    marginSize={4}
                  />
                </div>

                <div className="payment-details">
                  <div className="payment-amount-display">
                    <div className="amount-label">Amount to Pay:</div>
                    <div className="amount-crypto">
                      {payment.pay_amount} {payment.pay_currency?.toUpperCase()}
                    </div>
                    <div className="amount-fiat">
                      ≈ {fiatSymbol(payment.price_currency)}{payment.price_amount} {fiatLabel(payment.price_currency)}
                    </div>
                  </div>

                  {/* Share Payment Link */}
                  <div className="share-link-container">
                    <label>Share Payment Link:</label>
                    <div className="share-link-display">
                      <code>{window.location.origin}/pay/{payment.id}</code>
                      <button
                        type="button"
                        className="copy-btn"
                        onClick={() => copyToClipboard(`${window.location.origin}/pay/${payment.id}`)}
                        title="Copy shareable link"
                        data-testid="copy-link-btn"
                      >
                        {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
                      </button>
                    </div>
                    <p className="share-hint">Send this link to your customer to complete payment</p>

                    <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="email"
                        placeholder="Email link to customer..."
                        value={formData.customer_email}
                        onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                        style={{ flex: 1, minWidth: 200, padding: '8px 10px', borderRadius: 6, border: '1px solid #374151', background: '#0f172a', color: '#e5e7eb' }}
                        data-testid="post-create-email-input"
                      />
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={sendEmailNow}
                        disabled={emailSending || !formData.customer_email}
                        style={{ padding: '8px 14px', whiteSpace: 'nowrap' }}
                        data-testid="send-email-btn"
                      >
                        <Send size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                        {emailSending ? 'Sending...' : 'Send Email'}
                      </button>
                    </div>
                    {emailSentTo && (
                      <p style={{ marginTop: 8, fontSize: 12, color: '#22c55e' }}>
                        ✓ Email queued for {emailSentTo}
                      </p>
                    )}
                  </div>

                  {payment.pay_address && (
                    <div className="address-container">
                      <label>Payment Address:</label>
                      <div className="address-display">
                        <code>{payment.pay_address}</code>
                        <button
                          className="copy-btn"
                          onClick={() => copyToClipboard(payment.pay_address)}
                        >
                          {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="payment-instructions">
                    <p><strong>How to pay:</strong></p>
                    <ol>
                      <li>Scan the QR code with your crypto wallet</li>
                      <li>Or copy the payment address above</li>
                      <li>Send exactly {payment.pay_amount} {payment.pay_currency?.toUpperCase()}</li>
                      <li>Wait for blockchain confirmation</li>
                    </ol>
                  </div>
                </div>
              </>
            )}

            {payment.payment_status === 'paid' && (
              <div className="success-message">
                <CheckCircle size={48} className="success-icon" />
                <h3>Payment Received!</h3>
                <p>Your payment has been successfully received and is being processed.</p>
                <div className="settlement-info">
                  <p><strong>Settlement Details:</strong></p>
                  <p>Amount: {fiatSymbol(payment.price_currency)}{payment.price_amount} {fiatLabel(payment.price_currency)}</p>
                  <p>Platform Fee: {fiatSymbol(payment.price_currency)}{payment.platform_fee_amount}</p>
                  <p>Your Net Amount: {fiatSymbol(payment.price_currency)}{payment.merchant_net_amount}</p>
                  <p className="settlement-timing">Funds will be deposited to your bank account within 24-48 hours</p>
                </div>
              </div>
            )}

            {payment.payment_status === 'completed' && (
              <div className="success-message">
                <CheckCircle size={48} className="success-icon" />
                <h3>Payment Completed!</h3>
                <p>Transaction has been fully processed and settled.</p>
              </div>
            )}

            {(payment.payment_status === 'failed' || payment.payment_status === 'expired') && (
              <div className="error-message">
                <AlertCircle size={48} />
                <h3>Payment {payment.payment_status === 'failed' ? 'Failed' : 'Expired'}</h3>
                <p>This payment could not be completed. Please create a new payment.</p>
              </div>
            )}

            <button className="btn-secondary" onClick={handleClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentModal;
